import {
  Add,
  AttachFile,
  Delete,
  FileCopy,
  InsertDriveFile,
  MoreVert,
} from '@mui/icons-material';
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEntityActions, useEntityChildren } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useCreateEntityModal } from 'components/createEntity';
import FileExplorer from 'components/FileExplorer';
import { useCurrentModal, useModal } from 'components/modal';
import React, { useMemo, useState } from 'react';
import { FinsightEntity } from 'shared';
import { useSWRConfig } from 'swr';

export function EntityFiles(props: { entity: FinsightEntity }) {
  const { entity } = props;
  const { deleteEntity, updateEntity } = useEntityActions();
  const workspaceId = useWorkspaceId();
  const { cache, mutate } = useSWRConfig();
  const children = useEntityChildren(entity.id);
  const { showCreateEntityModal } = useCreateEntityModal();
  const { showModal } = useModal();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<FinsightEntity | null>(null);

  const fileItems = useMemo<FinsightEntity[]>(() => {
    return (
      children.data
        ?.filter((x) => x.types[0] === 'blackframe.file')
        .sort((a, b) => {
          const aDate = new Date(a.created).getTime();
          const bDate = new Date(b.created).getTime();
          return bDate - aDate;
        }) ?? []
    );
  }, [children.data]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    file: FinsightEntity,
  ) => {
    setSelectedFile(file);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleDeleteFile = async () => {
    if (!selectedFile) return;

    await deleteEntity(selectedFile.id);
    Array.from(cache.keys())
      .filter((x) =>
        x.includes(`/workspace/${workspaceId}/entity/${entity.id}/children`),
      )
      .forEach((key) => mutate(key));
    handleMenuClose();
  };

  const handleChangeFile = () => {
    if (!selectedFile) return;

    const FilePickerModal = () => {
      const modalCtx = useCurrentModal();
      const [manualUrl, setManualUrl] = useState(
        selectedFile.description || '',
      );

      const handleFileSelect = async (file: unknown) => {
        if (selectedFile && file && typeof file === 'object' && 'url' in file) {
          const fileItem = file as { url: string; name: string };

          await updateEntity(selectedFile.id, {
            name: fileItem.name,
            description: fileItem.url,
          });
          Array.from(cache.keys())
            .filter((x) =>
              x.includes(
                `/workspace/${workspaceId}/entity/${entity.id}/children`,
              ),
            )
            .forEach((key) => mutate(key));
          modalCtx?.closeModal();
        }
      };

      const handleManualUrlSubmit = async () => {
        if (selectedFile && manualUrl) {
          await updateEntity(selectedFile.id, {
            description: manualUrl,
          });
          Array.from(cache.keys())
            .filter((x) =>
              x.includes(
                `/workspace/${workspaceId}/entity/${entity.id}/children`,
              ),
            )
            .forEach((key) => mutate(key));
          modalCtx?.closeModal();
        }
      };

      return (
        <Stack spacing={2}>
          <FileExplorer onChange={handleFileSelect} />
          <Divider />
          <Stack direction="row" spacing={2} p={2}>
            <TextField
              fullWidth
              size="small"
              label="Or enter URL manually"
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              placeholder="https://example.com/file.pdf"
            />
            <Button
              variant="contained"
              onClick={handleManualUrlSubmit}
              disabled={!manualUrl}
            >
              Set URL
            </Button>
          </Stack>
        </Stack>
      );
    };

    showModal('Select File', <FilePickerModal />);
    handleMenuClose();
  };

  const handleFileClick = (file: FinsightEntity) => {
    if (file.description) {
      window.open(file.description, '_blank');
    }
  };

  return (
    <Stack
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        minHeight: 400,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Typography variant="h6">Files</Typography>
        <Button
          startIcon={<Add />}
          size="small"
          onClick={() => showCreateEntityModal('blackframe.file', entity)}
        >
          Add File
        </Button>
      </Stack>

      {fileItems.length === 0 ? (
        <Stack alignItems="center" py={4} spacing={2}>
          <InsertDriveFile sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.secondary">
            No files yet
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            Add your first file to get started
          </Typography>
        </Stack>
      ) : (
        <List>
          {fileItems.map((file) => (
            <ListItem
              key={file.id}
              divider
              button
              onClick={() => handleFileClick(file)}
              sx={{
                cursor: file.description ? 'pointer' : 'default',
                '&:hover': file.description
                  ? {
                      bgcolor: 'action.hover',
                    }
                  : {},
              }}
            >
              <ListItemIcon>
                <InsertDriveFile />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                secondary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    {file.description && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {file.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          •
                        </Typography>
                      </>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {new Date(file.created).toLocaleDateString()}
                    </Typography>
                  </Stack>
                }
              />
              <ListItemSecondaryAction>
                <IconButton onClick={(e) => handleMenuOpen(e, file)}>
                  <MoreVert />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleChangeFile}>
          <ListItemIcon>
            <AttachFile fontSize="small" />
          </ListItemIcon>
          Change File
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <FileCopy fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteFile} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Stack>
  );
}
